import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import {
  Users, FileText, DollarSign, Mail, Calendar,
  CreditCard, Shield, ChevronDown, ChevronUp,
  Trash2, Edit, Plus, X, Save, Check, Key, UserPlus, Ban, TrendingUp
} from "lucide-react";

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [changelog, setChangelog] = useState({ shipped: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [editingUser, setEditingUser] = useState(null);
  const [editingPlan, setEditingPlan] = useState("");
  const [showAddShipped, setShowAddShipped] = useState(false);
  const [showAddUpcoming, setShowAddUpcoming] = useState(false);
  const [newShipped, setNewShipped] = useState({ version: "", date: "", title: "", items: [""] });
  const [newUpcoming, setNewUpcoming] = useState({ title: "", description: "", status: "planned" });
  // New state for admin actions
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", password: "", name: "", plan: "free" });
  const [actionMsg, setActionMsg] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const getDateParams = (range) => {
    const now = new Date();
    let start = "", end = "";
    if (range === "today") {
      start = end = now.toISOString().split("T")[0];
    } else if (range === "7d") {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      start = s.toISOString().split("T")[0]; end = now.toISOString().split("T")[0];
    } else if (range === "30d") {
      const s = new Date(now); s.setDate(s.getDate() - 30);
      start = s.toISOString().split("T")[0]; end = now.toISOString().split("T")[0];
    } else if (range === "90d") {
      const s = new Date(now); s.setDate(s.getDate() - 90);
      start = s.toISOString().split("T")[0]; end = now.toISOString().split("T")[0];
    } else if (range === "this_month") {
      start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      end = now.toISOString().split("T")[0];
    } else if (range === "last_month") {
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = lm.toISOString().split("T")[0];
      end = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0];
    } else if (range === "this_year") {
      start = `${now.getFullYear()}-01-01`;
      end = now.toISOString().split("T")[0];
    } else if (range === "custom" && customStart && customEnd) {
      start = customStart; end = customEnd;
    }
    return { start, end };
  };

  useEffect(() => {
    if (user?.role !== "admin") { navigate("/dashboard"); return; }
    fetchAll();
  }, [user]);

  const handleDateChange = (range) => {
    setDateRange(range);
    if (range !== "custom") fetchAll(range);
  };

  const applyCustomDate = () => {
    if (customStart && customEnd) fetchAll("custom");
  };

  const fetchAll = async (range = dateRange) => {
    try {
      const { start, end } = getDateParams(range);
      let statsUrl = `${API}/admin/stats`;
      const params = [];
      if (start) params.push(`start_date=${start}`);
      if (end) params.push(`end_date=${end}`);
      if (params.length) statsUrl += `?${params.join("&")}`;
      
      const [statsRes, usersRes, changelogRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/changelog`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setChangelog(changelogRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (amount) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

  const handleUpdatePlan = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { plan: editingPlan });
      setEditingUser(null);
      showMsg("Plan updated");
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This removes all their data permanently.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      showMsg("User deleted");
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) { alert("Password must be at least 6 characters"); return; }
    try {
      await axios.put(`${API}/admin/users/${userId}/password`, { new_password: newPassword });
      setResetPasswordUser(null);
      setNewPassword("");
      showMsg("Password reset successfully");
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.password) { alert("Email and password required"); return; }
    try {
      await axios.post(`${API}/admin/users`, createForm);
      setShowCreateUser(false);
      setCreateForm({ email: "", password: "", name: "", plan: "free" });
      showMsg("User created");
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleCancelPlan = async (userId, email) => {
    if (!window.confirm(`Cancel plan for ${email}? They will be downgraded to free.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/cancel-plan`);
      showMsg("Plan cancelled");
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const showMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(""), 3000); };

  // Changelog handlers (unchanged)
  const handleAddShipped = async () => {
    try {
      await axios.post(`${API}/admin/changelog/shipped`, { ...newShipped, items: newShipped.items.filter(i => i.trim()) });
      setShowAddShipped(false);
      setNewShipped({ version: "", date: "", title: "", items: [""] });
      fetchAll();
    } catch (err) { alert("Failed to add release"); }
  };
  const handleDeleteShipped = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    try { await axios.delete(`${API}/admin/changelog/shipped/${id}`); fetchAll(); } catch (err) { alert("Failed"); }
  };
  const handleAddUpcoming = async () => {
    try {
      await axios.post(`${API}/admin/changelog/upcoming`, newUpcoming);
      setShowAddUpcoming(false);
      setNewUpcoming({ title: "", description: "", status: "planned" });
      fetchAll();
    } catch (err) { alert("Failed"); }
  };
  const handleDeleteUpcoming = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await axios.delete(`${API}/admin/changelog/upcoming/${id}`); fetchAll(); } catch (err) { alert("Failed"); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><p className="text-[14px] text-[#6B7280]">Loading admin panel...</p></div>;
  if (error) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="text-center"><Shield className="w-8 h-8 text-[#EF4444] mx-auto mb-3" /><p className="text-[15px] text-[#EF4444] mb-4">{error}</p><Link to="/dashboard" className="text-[14px] text-[#1A3DFF] hover:underline">Back to dashboard</Link></div></div>;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "users", label: `Users (${users.length})` },
    { id: "changelog", label: "Changelog" }
  ];

  return (
    <AppShell active="admin">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[#1A3DFF]" />
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Admin Panel</h1>
        </div>
        {actionMsg && <span className="text-[13px] font-medium text-[#059669] bg-[#ECFDF5] px-3 py-1.5 border border-[#A7F3D0]" data-testid="admin-action-msg">{actionMsg}</span>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F3F4F6] p-1 mb-6 sm:mb-8 w-fit" data-testid="admin-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-[13px] font-medium transition-colors ${activeTab === tab.id ? "bg-white shadow-sm text-[#0A0A0A]" : "text-[#6B7280] hover:text-[#0A0A0A]"}`}
            data-testid={`tab-${tab.id}`}>{tab.label}</button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && stats && (
        <>
          {/* Date Range Filter */}
          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5 mb-6" data-testid="admin-date-filter">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-[#6B7280]" />
              <span className="text-[13px] font-semibold text-[#0A0A0A]">Date Range</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: "all", label: "All Time" },
                { key: "today", label: "Today" },
                { key: "7d", label: "7 Days" },
                { key: "30d", label: "30 Days" },
                { key: "90d", label: "90 Days" },
                { key: "this_month", label: "This Month" },
                { key: "last_month", label: "Last Month" },
                { key: "this_year", label: "This Year" },
                { key: "custom", label: "Custom" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handleDateChange(key)}
                  className={`px-3 py-1.5 text-[12px] font-semibold transition-colors ${dateRange === key ? "bg-[#0A0A0A] text-white" : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"}`}
                  data-testid={`date-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {dateRange === "custom" && (
              <div className="flex items-center gap-2 mt-3">
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="border border-[#E5E7EB] px-3 py-1.5 text-[13px] focus:outline-none focus:border-[#0A0A0A]" data-testid="date-filter-start" />
                <span className="text-[12px] text-[#9CA3AF]">to</span>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="border border-[#E5E7EB] px-3 py-1.5 text-[13px] focus:outline-none focus:border-[#0A0A0A]" data-testid="date-filter-end" />
                <button onClick={applyCustomDate} className="px-3 py-1.5 bg-[#1A3DFF] text-white text-[12px] font-semibold hover:bg-[#1433CC]" data-testid="date-filter-apply">Apply</button>
              </div>
            )}
          </div>

          {/* Revenue Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div className="bg-[#0A0A0A] text-white p-4 sm:p-5" data-testid="admin-stat-mrr">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#10B981]" />
                <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">MRR</span>
              </div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em]">{fmt(stats.mrr)}</p>
            </div>
            <div className="bg-[#0A0A0A] text-white p-4 sm:p-5" data-testid="admin-stat-arr">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#1A3DFF]" />
                <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">ARR</span>
              </div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em]">{fmt(stats.arr)}</p>
            </div>
            <div className="bg-[#0A0A0A] text-white p-4 sm:p-5" data-testid="admin-stat-ltd-rev">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">LTD Revenue</span>
              </div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em]">{fmt(stats.ltd_revenue)}</p>
              <p className="text-[12px] text-[#6B7280] mt-1">{stats.lifetime_users} lifetime users</p>
            </div>
            <div className="bg-[#0A0A0A] text-white p-4 sm:p-5" data-testid="admin-stat-total-rev">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#10B981]" />
                <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Total Platform</span>
              </div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em]">{fmt(stats.total_platform_revenue)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5" data-testid="admin-stat-users">
              <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-[#1A3DFF]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Users</span></div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-[#0A0A0A]">{stats.total_users}</p>
              <p className="text-[12px] text-[#6B7280] mt-1"><span className="text-[#10B981]">+{stats.recent_signups_30d}</span> last 30d</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5" data-testid="admin-stat-invoices">
              <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#F59E0B]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Invoices</span></div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-[#0A0A0A]">{stats.total_invoices}</p>
              <p className="text-[12px] text-[#6B7280] mt-1">{stats.invoices.paid} paid, {stats.invoices.sent} sent</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5" data-testid="admin-stat-revenue">
              <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-[#10B981]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Paid Revenue</span></div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-[#0A0A0A]">{fmt(stats.total_revenue_paid)}</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-5" data-testid="admin-stat-plans">
              <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-[#8B5CF6]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Plans</span></div>
              <p className="font-display text-[22px] sm:text-[28px] tracking-[-0.02em] text-[#0A0A0A]">{stats.pro_users} <span className="text-[14px] text-[#6B7280]">pro</span></p>
              <p className="text-[12px] text-[#6B7280] mt-1">{stats.free_users} free, {stats.lifetime_users} lifetime</p>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="admin-invoice-breakdown">
            <h2 className="font-semibold text-[16px] text-[#0A0A0A] mb-4">Invoice Status</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Draft", count: stats.invoices.draft, color: "#6B7280" },
                { label: "Sent", count: stats.invoices.sent, color: "#F59E0B" },
                { label: "Paid", count: stats.invoices.paid, color: "#10B981" },
                { label: "Overdue", count: stats.invoices.overdue, color: "#EF4444" }
              ].map(s => (
                <div key={s.label} className="flex items-center gap-3">
                  <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div><p className="text-[20px] font-display text-[#0A0A0A]">{s.count}</p><p className="text-[12px] text-[#6B7280]">{s.label}</p></div>
                </div>
              ))}
            </div>
          </div>

          {/* Email Stats */}
          {stats.emails && (
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="admin-email-stats">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Emails Sent</h2>
                <span className="text-[12px] text-[#9CA3AF]">via Resend</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3">
                  <div className="flex items-center gap-2 mb-1"><Mail className="w-3.5 h-3.5 text-[#1A3DFF]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Total</span></div>
                  <p className="font-display text-[22px] tracking-[-0.02em] text-[#0A0A0A]">{stats.emails.total}</p>
                </div>
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3">
                  <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-3.5 h-3.5 text-[#10B981]" /><span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">Last 30d</span></div>
                  <p className="font-display text-[22px] tracking-[-0.02em] text-[#0A0A0A]">{stats.emails.last_30d}</p>
                </div>
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 col-span-2 sm:col-span-1">
                  <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide block mb-2">By Type</span>
                  <div className="space-y-1">
                    {Object.entries(stats.emails.by_type || {}).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-[13px] text-[#6B7280] capitalize">{type.replace(/_/g, ' ')}</span>
                        <span className="text-[13px] font-mono font-semibold text-[#0A0A0A]">{count}</span>
                      </div>
                    ))}
                    {Object.keys(stats.emails.by_type || {}).length === 0 && (
                      <p className="text-[12px] text-[#9CA3AF]">No emails sent yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top 20 Customers */}
          {stats.top_customers && stats.top_customers.length > 0 && (
            <div className="bg-white border border-[#E5E7EB] p-4 sm:p-6" data-testid="admin-top-customers">
              <h2 className="font-semibold text-[16px] text-[#0A0A0A] mb-4">Top Customers</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">#</th>
                      <th className="text-left px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Customer</th>
                      <th className="text-left px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Plan</th>
                      <th className="text-right px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Invoices</th>
                      <th className="text-right px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Billed</th>
                      <th className="text-right px-4 py-2 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.top_customers.map((c, i) => (
                      <tr key={i} className="border-b border-[#E5E7EB]">
                        <td className="px-4 py-3 text-[13px] text-[#9CA3AF] font-mono">{i + 1}</td>
                        <td className="px-4 py-3">
                          <p className="text-[14px] font-medium text-[#0A0A0A]">{c.name}</p>
                          <p className="text-[12px] text-[#6B7280]">{c.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 ${c.plan === 'lifetime' ? 'bg-[#FEF3C7] text-[#92400E]' : c.plan === 'pro' ? 'bg-[#EEF2FF] text-[#1A3DFF]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                            {c.plan}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-[14px] font-mono text-[#0A0A0A]">{c.invoice_count}</td>
                        <td className="px-4 py-3 text-right text-[14px] font-mono text-[#0A0A0A]">{fmt(c.total_billed)}</td>
                        <td className="px-4 py-3 text-right text-[14px] font-mono text-[#10B981]">{fmt(c.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* USERS TAB */}
      {activeTab === "users" && (
        <div className="space-y-4">
          {/* Create User Button */}
          <div className="flex justify-end">
            <button onClick={() => setShowCreateUser(!showCreateUser)} className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1A3DFF] hover:underline" data-testid="admin-create-user-btn">
              <UserPlus className="w-4 h-4" /> Create user
            </button>
          </div>

          {/* Create User Form */}
          {showCreateUser && (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 sm:p-5 space-y-3" data-testid="create-user-form">
              <h3 className="font-semibold text-[15px] text-[#0A0A0A]">Create New User</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="email" placeholder="Email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} className="input text-[14px]" data-testid="create-user-email" />
                <input type="password" placeholder="Password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} className="input text-[14px]" data-testid="create-user-password" />
                <input type="text" placeholder="Name" value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} className="input text-[14px]" data-testid="create-user-name" />
                <select value={createForm.plan} onChange={e => setCreateForm(p => ({ ...p, plan: e.target.value }))} className="input text-[14px]" data-testid="create-user-plan">
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={handleCreateUser} className="btn-primary text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="create-user-submit"><UserPlus className="w-4 h-4" /> Create</button>
                <button onClick={() => setShowCreateUser(false)} className="btn-outline text-[13px] px-4 py-2">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E7EB] overflow-hidden" data-testid="admin-users-table">
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">User</th>
                    <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Business</th>
                    <th className="text-left px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Plan</th>
                    <th className="text-right px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Invoices</th>
                    <th className="text-right px-5 py-3 font-semibold text-[11px] text-[#9CA3AF] uppercase tracking-wide">Joined</th>
                    <th className="px-5 py-3 w-36"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`admin-user-${u.id}`}>
                      <td className="px-5 py-4">
                        <p className="font-medium text-[14px] text-[#0A0A0A]">{u.name || "\u2014"}</p>
                        <p className="text-[12px] text-[#6B7280]">{u.email}</p>
                      </td>
                      <td className="px-5 py-4 text-[14px] text-[#6B7280]">{u.business_name || "\u2014"}</td>
                      <td className="px-5 py-4">
                        {editingUser === u.id ? (
                          <div className="flex items-center gap-2">
                            <select value={editingPlan} onChange={e => setEditingPlan(e.target.value)} className="border border-[#E5E7EB] text-[13px] px-2 py-1 bg-white" data-testid={`plan-select-${u.id}`}>
                              <option value="free">Free</option><option value="pro">Pro</option><option value="lifetime">Lifetime</option>
                            </select>
                            <button onClick={() => handleUpdatePlan(u.id)} className="p-1 text-[#10B981] hover:bg-[#ECFDF5]" data-testid={`save-plan-${u.id}`}><Check className="w-4 h-4" /></button>
                            <button onClick={() => setEditingUser(null)} className="p-1 text-[#6B7280] hover:bg-[#F3F4F6]"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <span className={`text-[11px] font-bold uppercase tracking-[0.05em] px-2 py-0.5 cursor-pointer ${
                            u.plan === "pro" ? "text-[#1A3DFF] bg-[#1A3DFF]/5 border border-[#1A3DFF]/20"
                            : u.plan === "lifetime" ? "text-[#F59E0B] bg-[#F59E0B]/5 border border-[#F59E0B]/20"
                            : "text-[#6B7280] bg-[#F3F4F6] border border-[#E5E7EB]"
                          }`} onClick={() => { setEditingUser(u.id); setEditingPlan(u.plan || "free"); }}>
                            {u.plan || "free"}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right font-mono text-[14px] text-[#0A0A0A]">{u.invoice_count}</td>
                      <td className="px-5 py-4 text-right text-[13px] text-[#6B7280]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "\u2014"}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setEditingUser(u.id); setEditingPlan(u.plan || "free"); }} className="p-1.5 hover:bg-[#F3F4F6] text-[#6B7280]" title="Edit plan" data-testid={`edit-user-${u.id}`}><Edit className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { setResetPasswordUser(u.id); setNewPassword(""); }} className="p-1.5 hover:bg-[#FEF3C7] text-[#6B7280] hover:text-[#D97706]" title="Reset password" data-testid={`reset-pw-${u.id}`}><Key className="w-3.5 h-3.5" /></button>
                          {(u.plan === "pro" || u.plan === "lifetime") && (
                            <button onClick={() => handleCancelPlan(u.id, u.email)} className="p-1.5 hover:bg-[#FEF2F2] text-[#6B7280] hover:text-[#EF4444]" title="Cancel plan" data-testid={`cancel-plan-${u.id}`}><Ban className="w-3.5 h-3.5" /></button>
                          )}
                          <button onClick={() => handleDeleteUser(u.id, u.email)} className="p-1.5 hover:bg-[#FEF2F2] text-[#9CA3AF] hover:text-[#EF4444]" title="Delete user" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        {/* Inline password reset */}
                        {resetPasswordUser === u.id && (
                          <div className="flex items-center gap-2 mt-2" data-testid={`reset-pw-form-${u.id}`}>
                            <input type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="border border-[#E5E7EB] text-[13px] px-2 py-1 flex-1" data-testid={`reset-pw-input-${u.id}`} />
                            <button onClick={() => handleResetPassword(u.id)} className="p-1 text-[#10B981] hover:bg-[#ECFDF5]" data-testid={`reset-pw-save-${u.id}`}><Check className="w-4 h-4" /></button>
                            <button onClick={() => setResetPasswordUser(null)} className="p-1 text-[#6B7280]"><X className="w-4 h-4" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="sm:hidden divide-y divide-[#E5E7EB]">
              {users.map(u => (
                <div key={u.id} className="p-4" data-testid={`admin-user-mobile-${u.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-[14px] text-[#0A0A0A]">{u.name || "\u2014"}</p>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${
                      u.plan === "pro" ? "text-[#1A3DFF] bg-[#1A3DFF]/5"
                      : u.plan === "lifetime" ? "text-[#F59E0B] bg-[#F59E0B]/5"
                      : "text-[#6B7280] bg-[#F3F4F6]"
                    }`}>{u.plan || "free"}</span>
                  </div>
                  <p className="text-[12px] text-[#6B7280]">{u.email}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[12px] text-[#6B7280]">{u.invoice_count} invoices</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditingUser(u.id); setEditingPlan(u.plan || "free"); }} className="p-1.5 text-[#6B7280]"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setResetPasswordUser(u.id); setNewPassword(""); }} className="p-1.5 text-[#6B7280]"><Key className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDeleteUser(u.id, u.email)} className="p-1.5 text-[#9CA3AF]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CHANGELOG TAB */}
      {activeTab === "changelog" && (
        <div className="space-y-6">
          <div className="bg-white border border-[#E5E7EB]" data-testid="admin-changelog-shipped">
            <div className="p-4 sm:p-5 border-b border-[#E5E7EB] flex items-center justify-between">
              <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Shipped Releases</h2>
              <button onClick={() => setShowAddShipped(!showAddShipped)} className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1A3DFF] hover:underline" data-testid="add-release-btn"><Plus className="w-4 h-4" /> Add release</button>
            </div>
            {showAddShipped && (
              <div className="p-4 sm:p-5 border-b border-[#E5E7EB] bg-[#F9FAFB] space-y-3" data-testid="add-release-form">
                <div className="grid grid-cols-3 gap-3">
                  <input type="text" placeholder="Version" value={newShipped.version} onChange={e => setNewShipped(p => ({ ...p, version: e.target.value }))} className="input text-[14px]" data-testid="release-version" />
                  <input type="date" value={newShipped.date} onChange={e => setNewShipped(p => ({ ...p, date: e.target.value }))} className="input text-[14px]" data-testid="release-date" />
                  <input type="text" placeholder="Title" value={newShipped.title} onChange={e => setNewShipped(p => ({ ...p, title: e.target.value }))} className="input text-[14px]" data-testid="release-title" />
                </div>
                <div className="space-y-2">
                  <label className="text-[12px] font-semibold text-[#6B7280] uppercase">Features</label>
                  {newShipped.items.map((item, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" value={item} onChange={e => { const items = [...newShipped.items]; items[i] = e.target.value; setNewShipped(p => ({ ...p, items })); }} className="input text-[14px] flex-1" placeholder="Feature" data-testid={`release-item-${i}`} />
                      {i === newShipped.items.length - 1 && <button onClick={() => setNewShipped(p => ({ ...p, items: [...p.items, ""] }))} className="p-2 text-[#1A3DFF]"><Plus className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAddShipped} className="btn-primary text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="save-release-btn"><Save className="w-4 h-4" /> Save</button>
                  <button onClick={() => setShowAddShipped(false)} className="btn-outline text-[13px] px-4 py-2">Cancel</button>
                </div>
              </div>
            )}
            <div className="divide-y divide-[#E5E7EB]">
              {changelog.shipped?.map((r, i) => (
                <div key={r.id || i} className="p-4 sm:p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div><span className="font-semibold text-[15px] text-[#0A0A0A]">v{r.version} — {r.title}</span><span className="text-[12px] text-[#9CA3AF] ml-3 font-mono">{r.date}</span></div>
                    <button onClick={() => handleDeleteShipped(r.id)} className="p-1.5 text-[#9CA3AF] hover:text-[#EF4444]" data-testid={`delete-release-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <ul className="space-y-1 ml-1">
                    {r.items?.map((item, j) => (<li key={j} className="flex items-start gap-2 text-[13px] text-[#6B7280]"><Check className="w-3.5 h-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />{item}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB]" data-testid="admin-changelog-upcoming">
            <div className="p-4 sm:p-5 border-b border-[#E5E7EB] flex items-center justify-between">
              <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Upcoming / Roadmap</h2>
              <button onClick={() => setShowAddUpcoming(!showAddUpcoming)} className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1A3DFF] hover:underline" data-testid="add-upcoming-btn"><Plus className="w-4 h-4" /> Add item</button>
            </div>
            {showAddUpcoming && (
              <div className="p-4 sm:p-5 border-b border-[#E5E7EB] bg-[#F9FAFB] space-y-3" data-testid="add-upcoming-form">
                <input type="text" placeholder="Feature title" value={newUpcoming.title} onChange={e => setNewUpcoming(p => ({ ...p, title: e.target.value }))} className="input text-[14px]" data-testid="upcoming-title" />
                <input type="text" placeholder="Short description" value={newUpcoming.description} onChange={e => setNewUpcoming(p => ({ ...p, description: e.target.value }))} className="input text-[14px]" data-testid="upcoming-description" />
                <select value={newUpcoming.status} onChange={e => setNewUpcoming(p => ({ ...p, status: e.target.value }))} className="input text-[14px]" data-testid="upcoming-status">
                  <option value="planned">Planned</option><option value="in progress">In Progress</option><option value="shipped">Shipped</option>
                </select>
                <div className="flex gap-2">
                  <button onClick={handleAddUpcoming} className="btn-primary text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="save-upcoming-btn"><Save className="w-4 h-4" /> Save</button>
                  <button onClick={() => setShowAddUpcoming(false)} className="btn-outline text-[13px] px-4 py-2">Cancel</button>
                </div>
              </div>
            )}
            <div className="divide-y divide-[#E5E7EB]">
              {changelog.upcoming?.map((item, i) => (
                <div key={item.id || i} className="p-4 sm:p-5 flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-[14px] text-[#0A0A0A]">{item.title}</span>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${
                        item.status === "in progress" ? "text-[#F59E0B] bg-[#FEF3C7]"
                        : item.status === "shipped" ? "text-[#10B981] bg-[#ECFDF5]"
                        : "text-[#1A3DFF] bg-[#1A3DFF]/5"
                      }`}>{item.status}</span>
                    </div>
                    <p className="text-[13px] text-[#6B7280]">{item.description}</p>
                  </div>
                  <button onClick={() => handleDeleteUpcoming(item.id)} className="p-1.5 text-[#9CA3AF] hover:text-[#EF4444] flex-shrink-0" data-testid={`delete-upcoming-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default AdminDashboard;
